import React, { useEffect, useState } from "react";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import * as _ from "lodash-es";
import { FormattedNumber } from "react-intl";
import SortOrderEnum from "../../../../models/SortOrderEnum";
import UIService from "../../../../services/UIService";
import ExcelJS from "exceljs";
import moment from "moment-timezone";
import {
    ReportSalesByUserProductDetailResponseDTO,
    IUserWithProducts,
    IUserProductDetail,
} from "../../../../models/DTO/ReportSalesByUserProductDetailResponseDTO";
import IProcessed_Report from "../../../../models/IProcessed_Report";
import ISupplierModel from "../../../../models/ISupplierModel";
import IVendorModel from "../../../../models/IVendorModel";
import IUser from "../../../../models/IUser";

type Props = {
    supplierModel: ISupplierModel;
    vendorModel: IVendorModel;
    userModel: IUser;
    processedReport: IProcessed_Report;
};

const ReportSalesByUserProductDetail = (props: Props) => {
    const [reportData, setReportData] = useState<ReportSalesByUserProductDetailResponseDTO>(
        {} as ReportSalesByUserProductDetailResponseDTO
    );
    const [users, setUsers] = useState<IUserWithProducts[] | undefined>(undefined);
    const [totalAmount, setTotalAmount] = useState<number | undefined>(undefined);
    const [totalOrders, setTotalOrders] = useState<number | undefined>(undefined);
    const [totalItems, setTotalItems] = useState<number | undefined>(undefined);
    const [periodStart, setPeriodStart] = useState<string | undefined>(undefined);
    const [periodEnd, setPeriodEnd] = useState<string | undefined>(undefined);
    const [orderIssues, setOrderIssues] = useState<{ name: string; message: string; guid: string; user: string }[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());

    useEffect(() => {
        onGetReportData(props.processedReport.report_data);
    }, [props.processedReport.report_data]);

    const onGetReportData = (data: ReportSalesByUserProductDetailResponseDTO) => {
        const { periodStart, periodEnd, totalAmount, totalOrders, totalItems, users, ordersWithIssues } = data;

        const orderIssuesArr: { name: string; message: string; guid: string; user: string }[] = [];

        if (ordersWithIssues) {
            const ordersWithIssuesGuids = Object.keys(ordersWithIssues);

            for (const orderGuid of ordersWithIssuesGuids) {
                const details = ordersWithIssues[orderGuid];
                orderIssuesArr.push({
                    ...details,
                    guid: orderGuid,
                });
            }
        }

        setReportData(data);
        setPeriodStart(periodStart);
        setPeriodEnd(periodEnd);
        setUsers(users);
        setTotalAmount(totalAmount);
        setTotalOrders(totalOrders);
        setTotalItems(totalItems);
        setOrderIssues(orderIssuesArr);
        setIsLoading(false);
    };

    const toggleUserExpanded = (email: string) => {
        const newExpanded = new Set(expandedUsers);
        if (newExpanded.has(email)) {
            newExpanded.delete(email);
        } else {
            newExpanded.add(email);
        }
        setExpandedUsers(newExpanded);
    };

    const downloadExcel = () => {
        if (!reportData || !reportData.users) return;

        const workbook = new ExcelJS.Workbook();

        // Sheet 1: Users Summary
        const usersWs = workbook.addWorksheet("Users");
        usersWs.columns = [
            { header: "Salesperson", key: "salesperson" },
            { header: "Email", key: "email" },
            { header: "Total Orders", key: "totalOrders" },
            { header: "Amount", key: "amount" },
            { header: "Average Sale", key: "averageSale" },
        ];

        for (const cell of ["A1", "B1", "C1", "D1", "E1"]) {
            usersWs.getCell(cell).font = { bold: true };
        }

        for (const user of reportData.users) {
            usersWs.addRow({
                salesperson: user.name,
                email: user.email,
                totalOrders: user.totalOrders,
                amount: user.amount,
                averageSale: user.totalOrders > 0 ? user.amount / user.totalOrders : 0,
            });
        }

        // Sheet 2: Product Detail
        const detailWs = workbook.addWorksheet("Product Detail");
        detailWs.columns = [
            { header: "Salesperson", key: "salesperson" },
            { header: "Email", key: "email" },
            { header: "Product SKU", key: "sku" },
            { header: "Product Name", key: "productName" },
            { header: "Qty", key: "qty" },
            { header: "Amount", key: "amount" },
        ];

        for (const cell of ["A1", "B1", "C1", "D1", "E1", "F1"]) {
            detailWs.getCell(cell).font = { bold: true };
        }

        for (const user of reportData.users) {
            for (const product of user.products) {
                detailWs.addRow({
                    salesperson: user.name,
                    email: user.email,
                    sku: product.sku,
                    productName: product.name,
                    qty: product.qty,
                    amount: product.amount,
                });
            }
        }

        const periodStartFormatted = moment(reportData.periodStart).format("YYYY-MM-DD");
        const periodEndFormatted = moment(reportData.periodEnd).format("YYYY-MM-DD");

        workbook.xlsx.writeBuffer().then((data) => {
            const blob = new Blob([data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = `${periodStartFormatted}-${periodEndFormatted}-orderwerks-sales-by-user-product-detail-report.xlsx`;
            anchor.click();
            window.URL.revokeObjectURL(url);
        });
    };

    const renderProductsSubTable = (products: IUserProductDetail[]) => {
        const sortedProducts = _.orderBy(products, ["amount"], [SortOrderEnum.DESC]);

        let tableClass = "table-dark";
        if (UIService.isMainViewLight()) {
            tableClass = "table-light";
        }

        return (
            <div className="ms-4 mb-2 me-2">
                <table className={"table table-sm mb-0 " + tableClass}>
                    <thead>
                        <tr>
                            <th>
                                <OwLabel title={"sku"} />
                            </th>
                            <th>
                                <OwLabel title={"name"} />
                            </th>
                            <th className="text-end">
                                <OwLabel title={"qty"} />
                            </th>
                            <th className="text-end">
                                <OwLabel title={"amount"} />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedProducts.map((product, idx) => (
                            <tr key={idx}>
                                <td>{product.sku}</td>
                                <td>{product.name}</td>
                                <td className="text-end">
                                    <FormattedNumber value={product.qty} />
                                </td>
                                <td className="text-end">
                                    <FormattedNumber value={product.amount} maximumFractionDigits={2} style={"currency"} currency={"USD"} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderUserTable = () => {
        if (!users) return null;

        const sortedUsers = _.orderBy(users, ["amount"], [SortOrderEnum.DESC]);

        let tableClass = "table-dark";
        if (UIService.isMainViewLight()) {
            tableClass = "";
        }

        let averageSaleAllUsers = 0;
        if (totalAmount && totalOrders) {
            averageSaleAllUsers = totalAmount / totalOrders;
        }
        if (_.isNaN(averageSaleAllUsers)) {
            averageSaleAllUsers = 0.0;
        }

        return (
            <div className="table-responsive">
                <table className={"table " + tableClass}>
                    <thead>
                        <tr>
                            <th style={{ width: "30px" }}></th>
                            <th>
                                <OwLabel title={"name"} />
                            </th>
                            <th>
                                <OwLabel title={"email"} />
                            </th>
                            <th className="text-end">
                                <OwLabel title={"total_orders"} />
                            </th>
                            <th className="text-end">
                                <OwLabel title={"amount"} />
                            </th>
                            <th className="text-end">
                                <OwLabel title={"average_sale"} />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedUsers.map((user, index) => {
                            const isExpanded = expandedUsers.has(user.email);
                            const averageSale = user.totalOrders > 0 ? user.amount / user.totalOrders : 0;

                            return (
                                <React.Fragment key={index}>
                                    <tr onClick={() => toggleUserExpanded(user.email)} style={{ cursor: "pointer" }}>
                                        <td>
                                            <i className={isExpanded ? "fas fa-chevron-down" : "fas fa-chevron-right"} />
                                        </td>
                                        <td>{user.name}</td>
                                        <td>{user.email}</td>
                                        <td className="text-end">{user.totalOrders}</td>
                                        <td className="text-end">
                                            <FormattedNumber
                                                value={user.amount}
                                                maximumFractionDigits={2}
                                                style={"currency"}
                                                currency={"USD"}
                                            />
                                        </td>
                                        <td className="text-end">
                                            <FormattedNumber
                                                value={averageSale}
                                                maximumFractionDigits={2}
                                                style={"currency"}
                                                currency={"USD"}
                                            />
                                        </td>
                                    </tr>
                                    {isExpanded && (
                                        <tr>
                                            <td colSpan={6} className="p-0">
                                                {renderProductsSubTable(user.products)}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        <tr>
                            <td />
                            <td />
                            <td />
                            <td className="text-end">{totalOrders}</td>
                            <td className="text-end">
                                <FormattedNumber value={totalAmount!} maximumFractionDigits={2} style={"currency"} currency={"USD"} />
                            </td>
                            <td className="text-end">
                                <FormattedNumber
                                    value={averageSaleAllUsers}
                                    maximumFractionDigits={2}
                                    style={"currency"}
                                    currency={"USD"}
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="text-center">
                <OwTitle title={"loading_report_data"} leftIconName={"fas fa-circle-notch fa-spin"} />
            </div>
        );
    }

    return (
        <div className="ReportSalesByUserProductDetail mt-2 mb-2">
            <div>
                <OwLabel className={"me-3"} title={"excel"} leftIconClass={"fas fa-download"} fn={downloadExcel} />
            </div>
            <div>
                <b>
                    <OwLabel className={"me-2"} title={"report_period"} />
                </b>
                {periodStart} - {periodEnd}
                <div className={orderIssues.length ? "" : "d-none"}>
                    <div>
                        <OwLabel title={"report_issues"} leftIconClass={"fas fa-exclamation-triangle"} className={"text-warning"} />
                        <br />
                        <OwLabel title={"there_are_order_issues_report_not_completely_accurate"} />
                    </div>
                    <div>
                        {_.map(orderIssues, (issue, idx) => {
                            return (
                                <div key={idx}>
                                    <a href={`/orders/${issue.guid}/entry`}>{issue.name}</a> - <OwLabel title={issue.message} />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div>
                <OwTitle title={"users"} tag={"h5"} />
                {renderUserTable()}
            </div>
        </div>
    );
};

export default ReportSalesByUserProductDetail;
